// src/components/sections/video-section.tsx
"use client";

import React, { useRef, useEffect, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import Image from '@/components/OptimizedImage';

interface VideoSectionProps {
  title?: string;
  landscapeSrc: string;
  portraitSrc: string;
  landscapePoster?: string;
  portraitPoster?: string;
}

const VideoSection = ({
    title,
    landscapeSrc,
    portraitSrc,
    landscapePoster,
    portraitPoster,
}: VideoSectionProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showControlsTemporarily, setShowControlsTemporarily] = useState(false);

  // --- Centralized Video Control Function ---
  const togglePlayback = async (startTime = 0) => {
    const video = videoRef.current;
    if (!video) return;

    try {
        if (video.paused) {
            // Only set currentTime if a specific start time is provided, otherwise resume current time
            if (startTime > 0) {
                 video.currentTime = startTime;
            }
            await video.play();
            setIsPlaying(true);
        } else {
            video.pause();
            setIsPlaying(false);
        }
        setHasUserInteracted(true);
    } catch {
        // iOS Safari pauses video-only background media to save power;
        // the resulting AbortError is expected and not actionable.
    }

    if (isMobile) {
        setShowControlsTemporarily(true);
    }
  };

  useEffect(() => {
    const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Defer video loading until section is near viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.setAttribute('playsinline', '');

    if (isMobile && !hasUserInteracted) {
        togglePlayback(0);
    } else if (!isMobile && !hasUserInteracted) {
        video.load();
    }

  }, [isVisible, isMobile, hasUserInteracted]); 
  
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showControlsTemporarily) {
      timer = setTimeout(() => {
        setShowControlsTemporarily(false);
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [showControlsTemporarily]);


  const handleInteraction = () => togglePlayback(isMobile ? 0 : 0);
  
  // Ref to store the current time when paused on desktop leave
  const pausedTimeRef = useRef(0);

  const handleMouseEnter = () => {
    const video = videoRef.current;
    if (!isMobile && video && video.paused) {
        togglePlayback(pausedTimeRef.current);
    }
  };

  const handleMouseLeave = () => {
    // Video continues playing after mouse leaves
  };
  
  const currentSrc = isMobile ? portraitSrc : landscapeSrc;
  const currentPoster = isMobile ? portraitPoster : landscapePoster;
  const videoAspectRatioClass = isMobile ? 'aspect-[9/16]' : 'aspect-video';

  const isButtonVisible = isMobile 
    ? (showControlsTemporarily || !isPlaying)
    : (!isPlaying); 

  return (
    <section ref={sectionRef} className="py-12 md:py-20">
      <div className="container mx-auto px-4">
        
        {title && (
          <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-left">
              {title}
          </h2>
        )}

        <div
          className="relative overflow-hidden rounded-lg shadow-xl group"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: 'pointer' }}
        >
          {/* Poster image - always visible until video plays */}
          {currentPoster && (
            <Image
              src={currentPoster}
              alt=""
              fill
              sizes="100vw"
              className={`object-cover transition-opacity duration-500 ease-in-out z-10 pointer-events-none ${
                hasUserInteracted ? 'opacity-0' : 'opacity-100'
              }`}
            />
          )}

          <video
            ref={videoRef}
            key={currentSrc}
            src={isVisible ? currentSrc : undefined}
            loop
            muted
            playsInline
            controls
            preload="none"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            className={`w-full h-auto object-cover ${videoAspectRatioClass}`}
          >
            Your browser does not support the video tag.
          </video>
          
          <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
            <button 
              className={`
                p-4 bg-black/50 rounded-full shadow-lg transition-opacity duration-300 pointer-events-auto cursor-pointer
                focus:outline-none focus:ring-2 focus:ring-white/70
                ${ isButtonVisible ? 'opacity-100' : 'opacity-0' }
              `}
              aria-label={isPlaying ? "Pause Video" : "Play Video"}
              onClick={(e) => {
                e.stopPropagation();
                handleInteraction();
              }} 
            >
              {isPlaying ? (
                <Pause className="text-white h-8 w-8 fill-white" />
              ) : (
                <Play className="text-white h-8 w-8 fill-white ml-0.5" />
              )}
            </button>
          </div>

        </div>
      </div>
    </section>
  );
};

export default VideoSection;
